import {intervalToMillis} from './patternTime';

export interface ExpiryCheckResult {
    expired: boolean;
    reason?: string;
}

export interface SignalExpiryPolicy {
    priceDeviationThreshold: number;
    maxCandleAge: number;
}

export function checkPriceDeviation(
    currentPrice: number,
    entryPrice: number,
    threshold: number,
): ExpiryCheckResult {
    if (currentPrice <= 0 || entryPrice <= 0) {
        return {expired: false};
    }
    const deviation = Math.abs(currentPrice - entryPrice) / entryPrice;
    if (0 < threshold && deviation >= threshold) {
        return {
            expired: true,
            reason: `当前价格偏离入场价 ${(deviation * 100).toFixed(1)}%，超过 ${(threshold * 100)}% 阈值`,
        };
    }
    return {expired: false};
}

export function checkHoldingPeriodExpired(
    expectedHoldingUntil: number | null | undefined,
    expectedHoldingBars: number | null | undefined,
    signalFormedAt: number | null | undefined,
    interval: string,
    maxCandleAge: number,
): ExpiryCheckResult {
    // 首选：基于形成时间 + 预计持仓K线数计算过期时间（基线=形成时间）
    if (null != expectedHoldingBars && expectedHoldingBars > 0
        && null != signalFormedAt && signalFormedAt > 0) {
        const intervalMs = intervalToMillis(interval);
        if (intervalMs > 0) {
            const expiryTime = signalFormedAt + expectedHoldingBars * intervalMs;
            if (Date.now() >= expiryTime) {
                return {expired: true, reason: '预计持仓时间已到期'};
            }
        }
    }

    // 降级：基于 expectedHoldingUntil（旧数据兼容）
    if (null != expectedHoldingUntil && expectedHoldingUntil > 0) {
        if (Date.now() >= expectedHoldingUntil) {
            return {expired: true, reason: '预计持仓时间已到期'};
        }
    }

    // 兜底：maxCandleAge（信号形成后最大K线数）
    if (null != signalFormedAt && signalFormedAt > 0 && 0 < maxCandleAge) {
        const intervalMs = intervalToMillis(interval);
        const maxAge = signalFormedAt + maxCandleAge * intervalMs;
        if (Date.now() >= maxAge) {
            return {expired: true, reason: `信号已超过 ${maxCandleAge} 根K线兜底时间`};
        }
    }

    return {expired: false};
}

export function checkZoneBreak(
    zoneType: string | null | undefined,
    zoneUpper: number | null | undefined,
    zoneLower: number | null | undefined,
    currentPrice: number,
    latestClose: number,
): ExpiryCheckResult {
    if ('LOW' === zoneType && null != zoneLower && zoneLower > 0) {
        if (latestClose < zoneLower) {
            return {expired: true, reason: '支撑区已被收盘价跌破'};
        }
    }
    if ('HIGH' === zoneType && null != zoneUpper && zoneUpper > 0) {
        if (latestClose > zoneUpper) {
            return {expired: true, reason: '阻力区已被收盘价突破'};
        }
    }
    if ('RANGE' === zoneType && null != zoneUpper && null != zoneLower) {
        if (latestClose > zoneUpper) {
            return {expired: true, reason: '盘整箱体上界已被收盘价突破'};
        }
        if (latestClose < zoneLower) {
            return {expired: true, reason: '盘整箱体下界已被收盘价跌破'};
        }
    }
    return {expired: false};
}

export function checkAllExpiry(
    params: {
        currentPrice: number;
        entryPrice: number;
        expectedHoldingUntil?: number | null;
        expectedHoldingBars?: number | null;
        signalFormedAt?: number | null;
        interval: string;
        zoneType?: string | null;
        zoneUpper?: number | null;
        zoneLower?: number | null;
        latestClose?: number;
        policy: SignalExpiryPolicy;
    },
): ExpiryCheckResult {
    const deviation = checkPriceDeviation(
        params.currentPrice, params.entryPrice, params.policy.priceDeviationThreshold,
    );
    if (deviation.expired) return deviation;

    const holding = checkHoldingPeriodExpired(
        params.expectedHoldingUntil, params.expectedHoldingBars, params.signalFormedAt, params.interval, params.policy.maxCandleAge,
    );
    if (holding.expired) return holding;

    if (null != params.zoneType && null != params.latestClose) {
        const zone = checkZoneBreak(
            params.zoneType, params.zoneUpper, params.zoneLower,
            params.currentPrice, params.latestClose,
        );
        if (zone.expired) return zone;
    }

    return {expired: false};
}
